import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Download, Loader2, XCircle, ArrowRight, ShoppingBag, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIGITAL_TYPES = new Set(["music", "single", "ep", "album", "exclusive", "digital"]);

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [progress, setProgress] = useState(0);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    // Duplicate-prevention: check sessionStorage for already-confirmed sessions
    const cacheKey = `checkout_${sessionId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setOrder(data);
        setStatus("paid");
        return;
      } catch {}
    }

    if (polledRef.current) return;
    polledRef.current = true;

    let cancelled = false;
    const maxAttempts = 12;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      setProgress(Math.round(((attempt + 1) / maxAttempts) * 100));
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          setOrder(data);
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "abandoned") {
          setStatus("expired");
          return;
        }
      } catch {}
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const downloadUrl = order?.download_url || (order ? `${API}/orders/${order.order_id}/download` : "");
  const downloadUrlWav = order?.download_url_wav || "";
  const isDigital = order && (DIGITAL_TYPES.has(order.product_type) || order.source === "album" || order.source === "music");
  const isZip = order?.download_kind === "zip" || order?.source === "album";
  const expiresHrs = order?.download_expires_in_hours || 0;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5 pt-16 pb-12">
      <div className="w-full max-w-md text-center">

        {/* ── Loading ── */}
        {status === "loading" && (
          <div data-testid="checkout-loading" className="animate-fade-in-up">
            <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto animate-spin mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl mb-3">CONFIRMING PAYMENT</h1>
            <p className="text-white/40 text-sm mb-6">Please wait while we verify your purchase...</p>
            <div className="h-1 bg-white/5 rounded-full overflow-hidden max-w-xs mx-auto">
              <div
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-white/20 text-[10px] mt-3 uppercase tracking-wider">This usually takes a few seconds</p>
          </div>
        )}

        {/* ── Success ── */}
        {status === "paid" && (
          <div data-testid="checkout-success" className="animate-fade-in-up">
            {/* Gold check icon */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-full animate-ping" style={{ animationDuration: "2s" }} />
              <div className="relative w-20 h-20 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
              </div>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl mb-2">THANK YOU</h1>
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-1">Payment Successful</p>
            <div className="h-[1px] w-10 mx-auto bg-[#D4AF37]/30 my-5" />

            {/* Order details card */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-5 text-left mb-6">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-white text-sm font-semibold">{order.product_title}</p>
                  <p className="text-white/30 text-xs mt-0.5 capitalize">{isDigital ? "Digital Download" : order.product_type}</p>
                </div>
                <p className="text-[#D4AF37] font-bold text-lg">${(order.amount || 0).toFixed(2)}</p>
              </div>
              <div className="h-[1px] bg-white/5 mb-3" />
              <div className="flex items-center gap-2 text-xs text-white/30">
                <Mail className="w-3 h-3" />
                <span>Receipt sent to your email</span>
              </div>
            </div>

            {/* Digital download */}
            {isDigital && order.download_granted && (
              <div className="mb-6 space-y-2">
                <Button
                  data-testid="download-btn"
                  asChild
                  className="w-full bg-[#D4AF37] text-black hover:bg-white font-semibold py-4 uppercase tracking-wider text-sm h-auto touch-manipulation"
                >
                  <a href={downloadUrl} download>
                    <Download className="w-4 h-4 mr-2" />
                    {isZip ? `Download ZIP · ${order.product_type?.toUpperCase() || "ALBUM"}` : "Download MP3 (with Cover Art)"}
                  </a>
                </Button>
                {isZip && (
                  <p className="text-white/40 text-[11px] text-center">
                    All tracks · embedded metadata · cover art{order?.download_url_wav ? " · WAV available on request" : ""}
                  </p>
                )}
                {downloadUrlWav && !isZip && (
                  <Button
                    data-testid="download-wav-btn"
                    asChild
                    variant="outline"
                    className="w-full border-white/10 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] font-semibold py-3 uppercase tracking-wider text-xs h-auto touch-manipulation"
                  >
                    <a href={downloadUrlWav} download>
                      <Download className="w-4 h-4 mr-2" /> Download WAV (Lossless)
                    </a>
                  </Button>
                )}
                <Link
                  to="/my-downloads"
                  className="block text-center text-white/40 hover:text-[#D4AF37] text-[11px] uppercase tracking-wider transition-colors pt-1"
                >
                  Re-download anytime →
                </Link>
                <p className="text-white/25 text-[10px] mt-2 text-center">
                  Secure link · expires in {expiresHrs}h · limit {order?.download_max_attempts || 5} downloads
                </p>
              </div>
            )}

            {/* Physical order */}
            {!isDigital && (
              <div className="mb-6 bg-white/[0.02] border border-white/5 rounded-lg p-5 text-left">
                <ShoppingBag className="w-5 h-5 text-[#D4AF37] mb-2" />
                <p className="text-sm text-white/70 font-medium">Your order is being prepared</p>
                <p className="text-xs text-white/35 mt-1">You'll receive shipping updates at your email.</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild variant="outline" className="border-white/10 text-white/50 hover:border-white/30 hover:text-white px-6 py-3 uppercase tracking-wider text-xs h-auto touch-manipulation">
                <Link to="/store">Continue Shopping <ArrowRight className="w-3.5 h-3.5 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-white/10 text-white/50 hover:border-white/30 hover:text-white px-6 py-3 uppercase tracking-wider text-xs h-auto touch-manipulation">
                <Link to="/">Home</Link>
              </Button>
            </div>
          </div>
        )}

        {/* ── Error / Timeout ── */}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <div data-testid="checkout-failed" className="animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-[#8B0000]/10 border border-[#8B0000]/30 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-[#8B0000]" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mb-3">
              {status === "timeout" ? "STILL PROCESSING" : "PAYMENT INCOMPLETE"}
            </h1>
            <p className="text-white/40 text-sm mb-8 max-w-xs mx-auto">
              {status === "timeout"
                ? "Your payment is still being confirmed. Check your email for a receipt, or try again."
                : "The payment was not completed. No charges were made."}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-sm h-auto touch-manipulation">
                <Link to="/store">Return to Store</Link>
              </Button>
              <Button asChild variant="outline" className="border-white/10 text-white/50 hover:border-white/30 hover:text-white px-6 py-3 uppercase tracking-wider text-xs h-auto touch-manipulation">
                <Link to="/">Home</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
